import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { generateUniqueID, getCookieValue } from "src/constants/functions";
import { omsBaseUrl, api, omsLatestUrl, vitecompanyserviceurl, medvolApiUrl, isProd } from "src/constants/global-const";
import { addAuthHeader } from "src/utils/auth";
import { addAuthHeader2 } from "src/utils/auth2";
import * as Sentry from "@sentry/browser";

const instance: AxiosInstance = axios.create({
  baseURL: api + "/api/v1/counter",
});

const orderInstance: AxiosInstance = axios.create({
  baseURL: api,
});

const omsInstance: AxiosInstance = axios.create({
  baseURL: omsBaseUrl,
});

const omsLatestInstance: AxiosInstance = axios.create({
  baseURL: omsLatestUrl,
});

const medvolInstance: AxiosInstance = axios.create({
  baseURL: medvolApiUrl,
});

const getOmsLatestInstance = () => {
  return axios.create({
    baseURL: omsLatestUrl + "/api/v1/oms",
  });
};

const companyserviceurl: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl + "/api/v1/oms",
});

const companyserviceurl2: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl + "/api/v1/oms",
});

const companyserviceurlevent: AxiosInstance = axios.create({
  baseURL: vitecompanyserviceurl,
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
omsInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
orderInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
omsLatestInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
getOmsLatestInstance().interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
companyserviceurl.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
companyserviceurl.interceptors.response.use(
  (response) => response,
  (error: any) => {
    //Capture 504 errors in Sentry
    if (error?.response?.status === 504 || error?.response?.status === 502) {
      console.warn(`⚠️ ${error.response.status} Error encountered, logging to Sentry...`);
      Sentry.init({
        dsn: "https://1e9355902a3f7463a13da7f274d73d85@o4506504892448768.ingest.us.sentry.io/4508557618839552",

        //Set sampleRate to 1.0 to capture 100% errors
        sampleRate: 1.0,

        // Set tracesSampleRate to 0.05 to capture 5%
        // of transactions for performance monitoring.
        tracesSampleRate: 0.05,

        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["*"],
        // Capture Replay for 100% of sessions with an error

        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
        environment: isProd ? "prod" : "dev",
      });
      if (window.location.origin.toLowerCase().includes("medvol.in")) {
        Sentry.captureException(error, {
          level: "error",
          tags: { status: error.response.status },
          extra: {
            url: error.config?.url || "Unknown URL",
            method: error.config?.method || "Unknown Method",
            data: error.config?.data || "No Data",
            headers: error.config?.headers || "No Headers",
          },
        });
      }
    }
    return Promise.reject(error);
  }
);

companyserviceurlevent.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
companyserviceurl2.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader2(config));

export const getCounterByEmployeeDetails = async (query: any): Promise<AxiosResponse> => {
  return instance.get(`/details/employee${query}`);
};

//ACU utilization
export const getCounterByEmployee = async (query: any): Promise<AxiosResponse> => {
  return instance.get(`/details/order${query}`);
};

export const getCounterOrderDetailsById = (counterOrderId: string) => {
  return orderInstance.get(`/oms/counter/locations?counter_code=${counterOrderId}`);
};

export const getOrderIds = (
  counter_company_location_id: number,
  company_code: string,
  page_number: number,
  page_size: number,
  order_id_int?: number | null,
  counter_orders?: string | null
) => {
  return companyserviceurl.get(`/order/order_details_filtered?counter_company_location_id=${counter_company_location_id}`, {
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
    },
    params: {
      page_number: page_number,
      page_size: page_size,
      company_code: company_code,
      order_id_int: order_id_int,
      counter_orders: counter_orders,
    },
  });
};

export const getCounterOrderProductsById = (counterLocationCode: string | undefined, item_code?: string, product_name?: string) => {
  return orderInstance.get(
    `/oms/counter/products/location?counter_company_location_id=${counterLocationCode}&item_code=${item_code}&product_name=${product_name}`
  );
};

export const getCounterOrderProductsByItemCode = (counterLocationCode: string | undefined, item_code?: string) => {
  return orderInstance.get(`/oms/counter/products/location?counter_company_location_id=${counterLocationCode}&exact_item_code=${item_code}`);
};

export const postPlaceOrder = (data: any, company_code: string) => {
  return companyserviceurlevent.post(`/event/order`, data, {
    validateStatus(status: number) {
      return status >= 200 && status <= 500;
    },
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
    },
  });
};

export const getStockist = (counterLocationCode: string, clusterCodes: string[] | null) => {
  let url = `oms/counter/company/stockist?counter_company_location_id=${counterLocationCode}&cluster_codes=${clusterCodes}&default_only=true`;

  // if (iSalesRequired) {
  //   url += `&iSalesRequired=true`;
  //   console.log(iSalesRequired)
  // }

  return orderInstance.get(url);
};

export const getDistrictStockist = (counterLocationCode: string, clusterCodes: string | null) => {
  return orderInstance.get(`oms/counter/company/district/stockist?counter_company_location_id=${counterLocationCode}&cluster_codes=${clusterCodes}`);
};

// export const getDistrictStockist = (counterLocationCode: string, clusterCodes: string[]) => {
//   return orderInstance.get(`oms/counter/company/district/stockist?counter_company_location_id=${counterLocationCode}&cluster_codes=${clusterCodes}`);
// };

export const getActionPermission = (position_code: string, order_id: number, company_code: string) => {
  return companyserviceurl.get(`/order/action-list?position_code=${position_code}&order_id=${order_id}`, {
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
      "Cache-Control": "no-cache",
    },
  });
};

export const orderApproveRejectActionPermission = (position_code: string, action: string, order_id: number, company_code: string) => {
  return companyserviceurl.get(`/order/is-action-allowed?position_code=${position_code}&action=${action}&order_id=${order_id}`, {
    headers: {
      company_code: company_code,
    },
  });
};

export const getOrderApprovalHierarchy = (order_id: number, company_code: string) => {
  return companyserviceurl.get(`/order/hierarchy?order_id=${order_id}`, {
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code || 0,
    },
  });
};

export const putAcceptOrderRequest = (payload: any, company_code: string) => {
  return companyserviceurlevent.post(`event/order/approve`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const putRejectOrderRequest = (payload: any, company_code: string) => {
  return companyserviceurlevent.post(`event/order/reject`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const pendingOrderDetails = (
  position_code: number,
  pageIndex: number,
  pageSize: number,
  globalSearch: string,
  company_code: string,
  company_divisioncode: string,
  stockist_id: string,
  order_status_id: string,
  order_mode: string
  // sortField: string,
  // sortOrder: string
) => {
  return companyserviceurl.get(`order/details_all`, {
    headers: {
      // "machine-token": "851f7a311679bb4213fbac68bac413cef95cc0c4a9f4610f44a612fa03a5e88f",
      company_code: company_code,
    },
    params: {
      page_number: pageIndex,
      page_size: pageSize,
      pending: true,
      position_code: position_code,
      search: globalSearch,
      company_code: company_code,
      company_divisioncode: company_divisioncode,
      company_stockist_code: stockist_id,
      status_id: order_status_id,
      order_mode: order_mode,
      // order_by: sortField,
      // ordering: sortOrder,
    },
  });
};

// fetch stockist filter data
export const fetchStockistFilterData = (company_code: string) => {
  return orderInstance.get(`/api/v1/stockist/details`, {
    params: {
      company_code: company_code,
      active_status: "Y",
    },
  });
};

export const fetchStatusDetails = (company_code: string) => {
  return companyserviceurl.get(`/order/status-options-list`, {
    headers: {
      company_code: company_code,
    },
    params: {
      company_code: company_code,
    },
  });
};

export const updateExDefault = (counter_company_location_id: string, selectedStockistIds: string[]) => {
  const body = {
    counter_company_location_id: counter_company_location_id,
    counter_stockist_Lnk_Id: selectedStockistIds,
  };
  return orderInstance.put(`oms/counter/updateExDefault`, body);
};

export const getOMSComments = (order_id: number, company_code: string, payload: any) => {
  return companyserviceurl.get(`/order/remarks`, {
    params: { order_id, ...payload },
    headers: {
      company_code: company_code,
    },
  });
};

export const postNewOMSComment = (data: any, primary_key: number, company_code: string) => {
  return companyserviceurlevent.post(
    `/event/order/remarks`,
    { data, event_type: "ORDER_REMARKS_ADD", primary_key },
    {
      headers: {
        company_code: company_code,
      },
    }
  );
};

export const uploadAttachment = (data: any, company_code: string) => {
  return companyserviceurl.post(`/order/upload`, data, {
    headers: { company_code },
  });
};

export const verifyOrderInvoice = async (data: any) => {
  const token = getCookieValue("idToken");

  if (!token) throw new Error("No token found");

  return medvolInstance.post(
    `/api/CounterPendingInvoices_ForV2`,

    data,

    {
      headers: {
        Authorization: token,
      },
    }
  );
};

export const addCounterStockist = (payload: any) => {
  return orderInstance.post(`event/counter/stockist/add`, payload)
    .catch((error) => {
      if (error.response?.status === 400) {
        // Ignore the error and return a resolved promise to continue flow
        return Promise.resolve(error.response);
      }
      // For other errors, rethrow to let the caller handle them
      return Promise.reject(error);
    });
};

export const addCounterStockistActiveInactive = (payload: any) => {
  return orderInstance.put(`event/counter/stockist/active-inactive`, payload);
};

export const getOrderFuidDetails = (order_code: string, page_number: number, page_size: number, company_code: string) => {
  return companyserviceurl.get(`/order/fuids?order_code=${order_code}&page_number=${page_number}&page_size=${page_size}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getOrderInvoiceDetails = (order_id_int: number, page_number: number, page_size: number, company_code: string) => {
  return companyserviceurl.get(`/order/invoices?order_id_int=${order_id_int}&page_number=${page_number}&page_size=${page_size}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getPostPermission = (
  position_code: number,
  order_group_ids: number[],
  company_code: string,
  actor_type: string,
  role: string,
  location_code: string,
  counter_company_location_id: number
) => {
  return orderInstance.get(`/api/v1/employee/upperhierarchy/flat-hierarchy`, {
    params: { position_code, order_group_ids, company_code, actor_type, role, location_code, counter_company_location_id },
  });
};

export const replaceStockist = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/replace-stockist`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const updateCashDiscount = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/update-cash-discount`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const updateOrderType = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/update-order-type`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const updateOrderStatus = (payload: any, company_code: string) => {
  return companyserviceurlevent.put(`/event/order/update-order-status`, payload, {
    headers: {
      company_code: company_code,
    },
  });
};

export const updateIndphUinValidation = (payload: any) => {
  return orderInstance.post(`/event/counter/indph/uin-validation`, payload);
};

export const sendMailToStockist = (payload: any) => {
  return orderInstance.post(`/novu?MessageGroupId=${generateUniqueID()}&MessageDeduplicationId=${generateUniqueID()}`, payload);
};

export const getMailStocksitDetails = (stockistCompanyCode: string) => {
  return orderInstance.get(`oms/stockist/company/details?stockist_company_code=${stockistCompanyCode}`);
};

export const getCounterCompanyStockist = (counterLocationCode: string, clusterCodes: string | null) => {
  return orderInstance.get(`oms/counter/company/stockist?counter_company_location_id=${counterLocationCode}&cluster_codes=${clusterCodes}`);
};

export const getCompanyClusterStockist = (company_code: string | number, clusterCodes: string | null) => {
  return orderInstance.get(`/oms/stockist/all?comapny_code=${company_code}&cluster_codes=${clusterCodes}`);
};

export const getUinRequired = (divisionCode: any[], counterCode: string) => {
  return orderInstance.get(`/api/v1/cda/uin-validated`, {
    params: {
      company_divisioncode: divisionCode.join(","),
      counter_code: counterCode,
    },
  });
};

export const getEmailContactDetails = (counterCode: string, locationCode: string | number, companyCode: string | number) => {
  return orderInstance.get(
    `/oms/counter/counter-ffdiv-company-location?Counter_Code=${counterCode}&Location_Code=${locationCode}&Company_Code=${companyCode}&lowest_and_manager=true&keys=CounterFFDivisionLnk.counter_id%2Cdrph_lnk_type.dr_ph_lnk_code%2Ccounter.mci_register_number%2Ccounter.drug_license_number%2Ccounter.gst_status%2Ccounter.gst_number%2Ccompany.name`
  );
};

export const downloadAttachment = (file_path: string) => {
  return companyserviceurl.get(`/order/get-file?file_key=${file_path}`, {
    headers: {
      company_code: 618,
    },
  });
};

export const getCounterLocations = (counter_id: number | string, comapny_code: number | string, company_stockist_code: number | string) => {
  return orderInstance.get(
    `/oms/counter/company/locations?counter_id=${counter_id}&company_code=${comapny_code}&company_stockist_code=${company_stockist_code}`
  );
};

export const getLocationsOfCounter = (division_code: number | string, excluded_location_id?: number | string, search?: any, page?: number) => {
  const params: Record<string, any> = {
    company_divisioncode: division_code,
  };
  if (excluded_location_id) {
    params.excluded_location_id = excluded_location_id;
  }
  if (search?.trim()) {
    params.search = search.trim();
  }
  if (page) {
    params.page = page;
  }

  return orderInstance.get("/oms/counter/company/counterlocations", { params });
};

export const updateOrderLocation = (payload: any) => {
  return companyserviceurlevent.put(`/event/order/location-shift`, payload);
};

export const getOrderProductsForMapping = (order_codes: string) => {
  return companyserviceurl.get(`/order/order_details_filtered?order_codes=${order_codes}&keys=orders.company_divisioncode%2Corderproducts.product_name%2Corderproducts.item_code%2Corders.division_name`);
};

export const getProductDetails = (item_code: string, company_divisioncode: string) => {
  return orderInstance.get(`/api/v1/product/details?item_code=${item_code}&company_divisioncode_visible=true&company_divisioncode=${company_divisioncode}`);
};

export const updateCashDiscountData = (counter_company_location_id: number, company_divisioncode: string) => {
  return orderInstance.get(`/oms/counter/cashdiscount-change?counter_company_location_id=${counter_company_location_id}&company_divisioncode=${company_divisioncode}`);
};

export const getAttachmentProofs = (counter_id: number) => {
  return orderInstance.get(`/api/v1/counter/attachmentproofs?counter_id=${counter_id}`);
};

export const getUinDetails = (counter_ff_div_lnk_id: number) => {
  return orderInstance.get(`/oms/counter/uin_details?counter_ff_div_lnk_id=${counter_ff_div_lnk_id}`);
};

export const getOrderDetails = (order_code: string) => {
  return companyserviceurl2.get(`/order/v2/${order_code}`);
};

export const getOrderChangeValidation = (payload: any) => {
  const token = getCookieValue("idToken");

  if (!token) throw new Error("No token found");

  return medvolInstance.post(`/api/orderchangevalidation`, payload, {
    headers: {
      Authorization: token,
    },
  });
};

export const getCounterNames = (counter_company_location_id: number) => {
  return orderInstance.get(`/api/v1/counter/names?counter_company_location_id=${counter_company_location_id}&city=true`);
};

export const getOrderDetailsByOrderCode = (orderCode: string, company_code: number) => {
  return companyserviceurl2.get(`/order/${orderCode}?total_amount=true&stockist=true`, {
    headers: {
      company_code: company_code,
    },
  });
};

export const getOrderDetailsByOrderCode2 = (order_code: string, company_code: string) => {
  return companyserviceurl2.get(`/order/v2/${order_code}`, {
    headers: {
      company_code: company_code || 0,
    },
  });
};

export const getCounterBlockedStatus = (counter_ff_div_lnk_id: number) => {
  return orderInstance.get(`/oms/counter/blocked?counter_ff_div_lnk_id=${counter_ff_div_lnk_id}`);
};

